import { Stage } from "konva/lib/Stage";
import { BoundingBox } from "@opstel/geo/BoundingBox";
import { create } from "zustand";
import { Rect, Size } from "@opstel/geo/types";
import { Point } from "@opstel/geo/Point";

type PlainPoint = { x: number; y: number };
type StagePosition = PlainPoint & { scale: number };
type ZoomableCanvasContextProps = StagePosition & {
  dragging: boolean;

  setPosition: (newPosition: StagePosition) => void;
  setZoomLevel: (newScale?: number) => void;
  setDragging: (draggingState: boolean) => void;
  zoomToFit: (stage: Stage, locationBoundingBox: BoundingBox) => void;
};

export const useZoomableCanvas = create<ZoomableCanvasContextProps>((set, _) => ({
  scale: 1,
  x: 0,
  y: 0,
  dragging: false,

  setPosition: ({ x, y, scale }) => set({ x, y, scale }),
  setZoomLevel: (newScale?: number) => {
    if (!newScale || Number.isNaN(newScale)) {
      return;
    }
    set({ scale: newScale });
  },
  setDragging: (dragging) => set({ dragging }),
  zoomToFit: (stage: Stage, locationBoundingBox: BoundingBox) => {
    const stageSize = {
      width: stage.width(),
      height: stage.height(),
    };

    const stagePosition = centerStageTo({
      rect: locationBoundingBox,
      stageSize,
      zoomToFit: true,
    });

    stage.position(stagePosition);
    set({ ...stagePosition });
  },
}));

interface CenterStageToProps {
  stageSize: Size;
  rect: Rect;
  zoomToFit?: boolean;
}
function centerStageTo({ stageSize, rect, zoomToFit = false }: CenterStageToProps): StagePosition {
  const scale = zoomToFit
    ? Math.min(stageSize.width / rect.width, stageSize.height / rect.height) * 0.95 // 5% margin
    : 1;
  const center = new Point(
    stageSize.width / 2 - rect.center.x * scale,
    stageSize.height / 2 - rect.center.y * scale,
  );

  return {
    x: center.x,
    y: center.y,
    scale,
  };
}
